import type { ReactNode } from 'react';

import MuiStepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Typography from '@mui/material/Typography';
import StepButton from '@mui/material/StepButton';

import { useWizardContext } from './contexts/WizardContext/hooks/useWizardContext.hook';
import type { WizardStep } from './types/step.type';

type LabelProps =
  | Readonly<{
      error: boolean;
      optional: ReactNode;
    }>
  | Record<string, never>;

interface Props<
  V extends object, // Value
  VS extends object, // Validation Schema
  SE, // Step Enum
> {
  readonly activeStep: number;
  readonly steps: readonly WizardStep<V, VS, SE>[];
  readonly setCurrentStepId: (id: SE) => void;
}

export const Stepper = <V extends object, VS extends object, SE>({
  activeStep,
  steps,
  setCurrentStepId,
}: Readonly<Props<V, VS, SE>>) => {
  const { wizardError } = useWizardContext<V, VS, SE>();

  return (
    <MuiStepper
      activeStep={activeStep}
      alternativeLabel
      sx={{ mt: 2, overflowX: 'auto', overflowY: 'hidden' }}
    >
      {steps.map(({ id, title, isEnabled }, index) => {
        const labelProps: LabelProps | undefined =
          wizardError &&
          (wizardError.step === id ||
            (!wizardError.step && index === steps.length - 1))
            ? {
                optional: (
                  <Typography color="error" variant="caption">
                    Erreur
                  </Typography>
                ),
                error: true,
              }
            : undefined;

        return (
          <Step
            completed={index < activeStep}
            disabled={!isEnabled}
            key={title}
          >
            {index < activeStep ? (
              <StepButton
                color="inherit"
                // eslint-disable-next-line react/jsx-no-bind
                onClick={() => {
                  setCurrentStepId(id);
                }}
              >
                {title}
              </StepButton>
            ) : (
              <StepLabel
                error={labelProps?.error}
                optional={labelProps?.optional}
              >
                {title}
              </StepLabel>
            )}
          </Step>
        );
      })}
    </MuiStepper>
  );
};
